import React from 'react';
import {
  Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
  Chip, LinearProgress, TextField, Button, Stack, Divider, Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditNoteIcon from '@mui/icons-material/EditNote';
import Colors from '../../../components/ui/Colors';
import ColumnOverrideDialog from './ColumnOverrideDialog';

const STATUS_COLORS = {
  PENDING: 'default',
  ACCEPTED: 'success',
  OVERRIDDEN: 'info',
  OVERRIDE_REQUIRED: 'error',
};

const verdictChip = (verdict) => {
  if (verdict === 'VALID') {
    return <Chip size="small" color="success" icon={<CheckCircleIcon />} label="VALID" />;
  }
  return <Chip size="small" color="warning" icon={<WarningAmberIcon />} label="DEGRADED" />;
};

const statusChip = (status) => (
  <Chip size="small" color={STATUS_COLORS[status] || 'default'} label={status} variant="outlined" />
);

const columnDefByName = (metadata, name) => {
  const cols = (metadata && metadata.definition && metadata.definition.columns) || [];
  return cols.find((c) => c.name === name) || null;
};

export default function ColumnReviewPanel(props) {
  const {
    reviewState, metadata, busyColumn, onSubmitKnowledge, onAccept, onOverride,
  } = props;

  const columns = (reviewState && reviewState.columns) || {};
  const entries = Object.values(columns);

  const needsAction = (s) => s === 'PENDING' || s === 'OVERRIDE_REQUIRED';

  const sorted = [...entries].sort((a, b) => {
    // OVERRIDE_REQUIRED first (admin must act), then PENDING, then resolved.
    const rank = (s) => (s === 'OVERRIDE_REQUIRED' ? 0 : s === 'PENDING' ? 1 : 2);
    const ra = rank(a.review_status);
    const rb = rank(b.review_status);
    if (ra !== rb) return ra - rb;
    const aDeg = a.verdict === 'DEGRADED' ? 0 : 1;
    const bDeg = b.verdict === 'DEGRADED' ? 0 : 1;
    return aDeg - bDeg;
  });

  const pendingCount = entries.filter((c) => needsAction(c.review_status)).length;
  const overrideRequiredCount = entries.filter(
    (c) => c.review_status === 'OVERRIDE_REQUIRED',
  ).length;

  const [knowledgeDrafts, setKnowledgeDrafts] = React.useState({});
  const [overrideTarget, setOverrideTarget] = React.useState(null);

  const setDraft = (col, value) => {
    setKnowledgeDrafts((prev) => ({ ...prev, [col]: value }));
  };

  const handleSubmitKnowledge = async (columnName) => {
    const text = (knowledgeDrafts[columnName] || '').trim();
    if (!text) return;
    const ok = await onSubmitKnowledge(columnName, text);
    if (ok) setDraft(columnName, '');
  };

  const handleOpenOverride = (columnName) => {
    setOverrideTarget(columnName);
  };

  const handleCloseOverride = () => {
    setOverrideTarget(null);
  };

  const handleSubmitOverride = async (body) => {
    if (!overrideTarget) return;
    const ok = await onOverride(overrideTarget, body);
    if (ok) setOverrideTarget(null);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, borderColor: Colors.primary }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <EditNoteIcon sx={{ color: Colors.primary }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Awaiting your review
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Chip
          size="small"
          color={pendingCount > 0 ? 'warning' : 'success'}
          label={pendingCount > 0
            ? `${pendingCount} column${pendingCount === 1 ? '' : 's'} still need review`
            : 'All columns reviewed'}
        />
      </Box>

      {pendingCount > 0 && (
        <Alert
          severity={overrideRequiredCount > 0 ? 'warning' : 'info'}
          sx={{ mb: 2 }}
        >
          Confirm &amp; inject is locked until every column is accepted or manually overridden.
          {overrideRequiredCount > 0 && (
            <>
              {' '}
              <b>{overrideRequiredCount} column{overrideRequiredCount === 1 ? '' : 's'}</b>{' '}
              hit the max re-enrichment cycles and require a manual override.
            </>
          )}
        </Alert>
      )}

      {sorted.map((col) => {
        const isPending = col.review_status === 'PENDING';
        const isOverrideRequired = col.review_status === 'OVERRIDE_REQUIRED';
        const isDegraded = col.verdict === 'DEGRADED';
        const isBusy = busyColumn === col.column_name;
        const def = columnDefByName(metadata, col.column_name);
        const confidencePct = Math.round(((col.confidence || 0) * 100));

        return (
          <Accordion
            key={col.column_name}
            defaultExpanded={isOverrideRequired || (isPending && isDegraded)}
            disableGutters
            sx={{ mb: 1 }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
                <Typography sx={{ fontFamily: 'monospace', fontWeight: 600 }}>
                  {col.column_name}
                </Typography>
                {verdictChip(col.verdict)}
                {statusChip(col.review_status)}
                {col.cycle_count > 0 && (
                  <Chip size="small" variant="outlined" label={`cycle ${col.cycle_count}`} />
                )}
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">
                  Confidence {confidencePct}%
                </Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary">Confidence</Typography>
                <LinearProgress
                  variant="determinate"
                  value={confidencePct}
                  color={isDegraded ? 'warning' : 'success'}
                  sx={{ height: 8, borderRadius: 1, mt: 0.5 }}
                />
              </Box>

              {def && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Current LLM description</Typography>
                  <Typography variant="body2" sx={{ mt: 0.25 }}>
                    {def.description || <i>(none)</i>}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 0.75 }}>
                    <Chip size="small" label={`type: ${def.type || '—'}`} />
                    <Chip size="small" label={`redacted: ${def.redacted ? 'yes' : 'no'}`} />
                  </Stack>
                </Box>
              )}

              {Array.isArray(col.degradation_reasons) && col.degradation_reasons.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Why this is unclear</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.25 }}>
                    {col.degradation_reasons.map((r, i) => (
                      <li key={i}><Typography variant="body2">{r}</Typography></li>
                    ))}
                  </Box>
                </Box>
              )}

              {Array.isArray(col.user_knowledge) && col.user_knowledge.length > 0 && (
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="caption" color="text.secondary">Knowledge you have provided</Typography>
                  <Box component="ul" sx={{ m: 0, pl: 2.5, mt: 0.25 }}>
                    {col.user_knowledge.map((k, i) => (
                      <li key={i}><Typography variant="body2">{k}</Typography></li>
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ my: 1.5 }} />

              {isPending && (
                <>
                  <Typography variant="caption" color="text.secondary">Add knowledge</Typography>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    placeholder="e.g. This column is the patient enrollment date in ISO format."
                    value={knowledgeDrafts[col.column_name] || ''}
                    onChange={(e) => setDraft(col.column_name, e.target.value)}
                    disabled={isBusy}
                    sx={{ mt: 0.5, mb: 1 }}
                  />
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={() => handleOpenOverride(col.column_name)}
                      disabled={isBusy}
                    >
                      Override manually
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => onAccept(col.column_name)}
                      disabled={isBusy}
                      color="success"
                    >
                      Accept this column
                    </Button>
                    <Button
                      variant="contained"
                      onClick={() => handleSubmitKnowledge(col.column_name)}
                      disabled={isBusy || !(knowledgeDrafts[col.column_name] || '').trim()}
                      sx={{ backgroundColor: Colors.primary }}
                    >
                      {isBusy ? 'Re-enriching…' : 'Submit knowledge'}
                    </Button>
                  </Stack>
                </>
              )}

              {isOverrideRequired && (
                <>
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    Max re-enrichment cycles reached for this column. The LLM keeps producing
                    DEGRADED metadata even with the knowledge you supplied. Set the column
                    manually to continue.
                  </Alert>
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      variant="contained"
                      color="warning"
                      onClick={() => handleOpenOverride(col.column_name)}
                      disabled={isBusy}
                    >
                      Override manually
                    </Button>
                  </Stack>
                </>
              )}

              {!isPending && !isOverrideRequired && (
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                    {col.review_status === 'ACCEPTED' ? 'Accepted by admin' : 'Manually overridden'}
                  </Typography>
                </Stack>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}

      <ColumnOverrideDialog
        open={!!overrideTarget}
        columnName={overrideTarget}
        currentDefinition={overrideTarget ? columnDefByName(metadata, overrideTarget) : null}
        onClose={handleCloseOverride}
        onSubmit={handleSubmitOverride}
        submitting={busyColumn === overrideTarget}
      />
    </Paper>
  );
}
